import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from '../ui';
import { contentIngestionService, type DraftChange } from '../../services/contentIngestionService';

const humanize = (field: string) => field.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const printable = (value: unknown) => value == null || value === '' ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);

export default function DraftRecordDrawer({ eventId, change, onClose, onSaved }: {
  eventId: string;
  change: DraftChange | null;
  onClose: () => void;
  onSaved: (change: DraftChange) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fields = useMemo(() => change ? [...new Set([
    ...Object.keys(change.published_data ?? {}), ...Object.keys(change.proposed_data), ...Object.keys(change.approved_data),
  ])].filter((field) => !['id', 'event_id', 'created_at', 'updated_at'].includes(field)).sort() : [], [change]);

  useEffect(() => {
    setValues(Object.fromEntries(Object.entries(change?.approved_data ?? {}).map(([key, value]) => [key, printable(value) === '—' ? '' : printable(value)])));
    setError(null);
  }, [change]);

  async function save() {
    if (!change) return;
    setSaving(true); setError(null);
    try {
      const patch = Object.fromEntries(fields.map((field) => {
        const original = change.approved_data[field];
        const value = values[field] ?? '';
        if (typeof original === 'boolean') return [field, value === 'true'];
        if (typeof original === 'number') return [field, value === '' ? null : Number(value)];
        if (Array.isArray(original) || (original && typeof original === 'object')) {
          try { return [field, JSON.parse(value)]; } catch { return [field, value]; }
        }
        return [field, value || null];
      }));
      onSaved(await contentIngestionService.patchDraft(eventId, change.id, { patch }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to save draft'); }
    finally { setSaving(false); }
  }

  return <Modal open={Boolean(change)} onClose={onClose} title={change ? `Review ${String(change.approved_data.title ?? change.approved_data.display_name ?? 'draft')}` : 'Review draft'} size="xl">
    {change && <div className="space-y-4">
      <div className="overflow-x-auto"><table className="min-w-full text-left text-xs"><thead><tr className="text-gray-400"><th className="px-2 py-2">Field</th><th className="px-2 py-2">Published</th><th className="px-2 py-2">Uploaded</th><th className="px-2 py-2">AI suggestion</th><th className="px-2 py-2">Organizer-approved</th></tr></thead><tbody>{fields.map((field) => <tr key={field} className="border-t border-gray-800 align-top"><th className="px-2 py-2 text-gray-300">{humanize(field)}</th><td className="px-2 py-2 text-gray-500">{printable(change.published_data?.[field])}</td><td className="px-2 py-2 text-gray-300">{printable(change.proposed_data[field])}</td><td className="px-2 py-2 text-violet-300">{field === 'identity' ? printable(change.match_proposal?.rationale) : '—'}</td><td className="px-2 py-2"><input aria-label={humanize(field)} value={values[field] ?? ''} onChange={(event) => setValues((current) => ({ ...current, [field]: event.target.value }))} className="min-w-32 rounded border border-gray-700 bg-gray-950 px-2 py-1 text-gray-100" /></td></tr>)}</tbody></table></div>
      {Boolean(change.match_proposal?.rationale) && <p className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 text-sm text-violet-200"><strong>AI identity suggestion:</strong> {String(change.match_proposal?.rationale)}</p>}
      {change.issues.length > 0 && <ul className="space-y-1">{change.issues.map((issue) => <li key={issue.id} className={issue.severity === 'error' ? 'text-sm text-red-300' : 'text-sm text-amber-300'}>{issue.field ? `${humanize(issue.field)}: ` : ''}{issue.message}</li>)}</ul>}
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      <div className="flex justify-end gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={() => void save()}>Save draft</Button></div>
    </div>}
  </Modal>;
}
